import { useState, useEffect } from 'react';
import type { BlogPost, BlogFormData } from './types';
import { Copy, Eye, Pencil } from 'lucide-react';

interface BlogEditorProps {
  post?: BlogPost;
  onSave: () => void;
  onCancel: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

const INPUT = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all";
const LABEL = "block text-xs font-medium text-white/50 mb-1.5";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50";
const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm p-5";

const CATEGORIES = [
  { value: 'Getting Started', labelEs: 'Comenzando' },
  { value: 'Comparison', labelEs: 'Comparación' },
  { value: 'Advanced', labelEs: 'Avanzado' },
  { value: 'Tips', labelEs: 'Consejos' },
  { value: 'Case Studies', labelEs: 'Estudios de Caso' },
];

function BlogEditor({ post, onSave, onCancel }: BlogEditorProps) {
  const [formData, setFormData] = useState<BlogFormData>({
    title: '', titleEs: '', excerpt: '', excerptEs: '', content: '', contentEs: '',
    category: 'Getting Started', categoryEs: '', readTime: '5 min read',
    featuredImage: '', author: 'WaveBudget Team', tags: [], published: false,
    seoTitle: '', seoTitleEs: '', seoDescription: '', seoDescriptionEs: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [seoFieldErrors, setSeoFieldErrors] = useState({
    seoTitle: '', seoTitleEs: '', seoDescription: '', seoDescriptionEs: '',
  });

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title, titleEs: post.titleEs, excerpt: post.excerpt, excerptEs: post.excerptEs,
        content: post.content, contentEs: post.contentEs, category: post.category, categoryEs: post.categoryEs,
        readTime: post.readTime, featuredImage: post.featuredImage || '', author: post.author,
        tags: post.tags, published: post.published, seoTitle: post.seoTitle || '',
        seoTitleEs: post.seoTitleEs || '', seoDescription: post.seoDescription || '',
        seoDescriptionEs: post.seoDescriptionEs || '',
      });
    }
  }, [post]);

  const validateSeoField = (field: string, value: string, maxLength: number) => {
    const errors = { ...seoFieldErrors };
    if (value.length === 0) errors[field as keyof typeof seoFieldErrors] = 'Required';
    else if (value.length > maxLength) errors[field as keyof typeof seoFieldErrors] = `${maxLength - value.length} chars over`;
    else errors[field as keyof typeof seoFieldErrors] = '';
    setSeoFieldErrors(errors);
  };

  const handleInputChange = (field: keyof BlogFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'title' && !formData.seoTitle) {
      const v = value as string;
      setFormData(prev => ({ ...prev, seoTitle: v }));
      validateSeoField('seoTitle', v, 60);
    }
    if (field === 'titleEs' && !formData.seoTitleEs) {
      const v = value as string;
      setFormData(prev => ({ ...prev, seoTitleEs: v }));
      validateSeoField('seoTitleEs', v, 60);
    }
    if (field === 'excerpt' && !formData.seoDescription) {
      const v = value as string;
      setFormData(prev => ({ ...prev, seoDescription: v }));
      validateSeoField('seoDescription', v, 160);
    }
    if (field === 'excerptEs' && !formData.seoDescriptionEs) {
      const v = value as string;
      setFormData(prev => ({ ...prev, seoDescriptionEs: v }));
      validateSeoField('seoDescriptionEs', v, 160);
    }
    if (field === 'category') {
      const cat = CATEGORIES.find(c => c.value === value);
      if (cat) setFormData(prev => ({ ...prev, categoryEs: cat.labelEs }));
    }
    if (field === 'content' && !formData.excerpt) {
      const plain = (value as string).replace(/[#*`\[\]]/g, '').substring(0, 150);
      setFormData(prev => ({ ...prev, excerpt: plain + (plain.length >= 150 ? '…' : '') }));
    }
    if (field === 'contentEs' && !formData.excerptEs) {
      const plain = (value as string).replace(/[#*`\[\]]/g, '').substring(0, 150);
      setFormData(prev => ({ ...prev, excerptEs: plain + (plain.length >= 150 ? '…' : '') }));
    }
    if (field.startsWith('seo')) {
      const fieldMap: Record<string, number> = { seoTitle: 60, seoTitleEs: 60, seoDescription: 160, seoDescriptionEs: 160 };
      if (fieldMap[field]) validateSeoField(field, value as string, fieldMap[field]);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file (JPEG, PNG, GIF, WebP)'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image size should be less than 5MB'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/blog/admin/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!response.ok) { const txt = await response.text(); throw new Error(`Upload failed: ${response.status} ${txt}`); }
      const data = await response.json();
      handleInputChange('featuredImage', data.url);
      if (e.target) e.target.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally { setUploading(false); }
  };

  const generateSlug = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

  const previewLink = formData.title ? `/blog/${generateSlug(formData.title)}` : '';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(err => console.error('Failed to copy:', err));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const slug = post?.slug || generateSlug(formData.title);
      const submitData = { ...formData, slug, publishedAt: post?.publishedAt || new Date().toISOString(), updatedAt: new Date().toISOString() };
      const url = post ? `${API_URL}/api/blog/admin/posts/${post._id}` : `${API_URL}/api/blog/admin/posts`;
      const response = await fetch(url, {
        method: post ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });
      if (!response.ok) { const d = await response.json(); throw new Error(d.message || 'Failed to save post'); }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally { setLoading(false); }
  };

  const seoCharCls = (len: number, max: number) =>
    len > max ? 'text-red' : len > max * 0.7 ? 'text-orange' : 'text-green';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">{post ? 'Edit Article' : 'New Article'}</h1>
          <div className="flex gap-2">
            {formData.published && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green/15 text-green border border-green/20">Published</span>}
            {isPreview && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/20">Preview Mode</span>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setIsPreview(!isPreview)} className={BTN_SECONDARY}>
            {isPreview ? <><Pencil size={14} /> Edit Mode</> : <><Eye size={14} /> Preview</>}
          </button>
          {previewLink && (
            <button type="button" onClick={() => copyToClipboard(`${window.location.origin}${previewLink}`)} className={BTN_SECONDARY} title="Copy blog link">
              <Copy size={14} /> <span className="text-white/40 text-xs max-w-[160px] truncate">{previewLink}</span>
            </button>
          )}
          <button type="button" onClick={onCancel} className={BTN_SECONDARY}>Cancel</button>
          <button type="submit" form="blog-editor-form" disabled={loading} className={BTN_PRIMARY}>
            {loading ? 'Saving…' : 'Save Article'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}

      <form id="blog-editor-form" onSubmit={handleSubmit} className="space-y-6">
        {/* English + Spanish content side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* English */}
          <div className={CARD}>
            <h2 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">English Content</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Title *</label>
                  <input type="text" className={INPUT} value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} required maxLength={100} />
                </div>
                <div>
                  <label className={LABEL}>Category</label>
                  <select className={INPUT} value={formData.category} onChange={(e) => handleInputChange('category', e.target.value)} required>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={LABEL}>Excerpt *</label>
                <textarea className={INPUT} value={formData.excerpt} onChange={(e) => handleInputChange('excerpt', e.target.value)} required maxLength={200} rows={3} />
              </div>
              <div>
                <label className={LABEL}>Content (Markdown) *</label>
                <textarea className={`${INPUT} font-mono text-xs leading-relaxed`} value={formData.content} onChange={(e) => handleInputChange('content', e.target.value)} required rows={18} />
              </div>
            </div>
          </div>

          {/* Spanish */}
          <div className={CARD}>
            <h2 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Spanish Content</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Spanish Title *</label>
                  <input type="text" className={INPUT} value={formData.titleEs} onChange={(e) => handleInputChange('titleEs', e.target.value)} required maxLength={100} />
                </div>
                <div>
                  <label className={LABEL}>Spanish Category</label>
                  <input type="text" className={INPUT} value={formData.categoryEs} onChange={(e) => handleInputChange('categoryEs', e.target.value)} required />
                </div>
              </div>
              <div>
                <label className={LABEL}>Spanish Excerpt *</label>
                <textarea className={INPUT} value={formData.excerptEs} onChange={(e) => handleInputChange('excerptEs', e.target.value)} required maxLength={200} rows={3} />
              </div>
              <div>
                <label className={LABEL}>Spanish Content (Markdown) *</label>
                <textarea className={`${INPUT} font-mono text-xs leading-relaxed`} value={formData.contentEs} onChange={(e) => handleInputChange('contentEs', e.target.value)} required rows={18} />
              </div>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className={CARD}>
          <h2 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Article Settings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={LABEL}>Read Time</label>
              <input type="text" className={INPUT} value={formData.readTime} onChange={(e) => handleInputChange('readTime', e.target.value)} placeholder="5 min read" />
            </div>
            <div>
              <label className={LABEL}>Author *</label>
              <input type="text" className={INPUT} value={formData.author} onChange={(e) => handleInputChange('author', e.target.value)} required />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.published} onChange={(e) => handleInputChange('published', e.target.checked)}
                  className="w-4 h-4 rounded border border-white/20 bg-white/[0.05] accent-primary" />
                <span className="text-sm text-white/70 font-medium">Published</span>
              </label>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-3">
            <label className={LABEL}>Featured Image</label>
            <div className="flex flex-wrap gap-3">
              <label className={`${BTN_SECONDARY} cursor-pointer`}>
                {uploading ? 'Uploading…' : 'Upload Image'}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
              <input type="url" className={`${INPUT} flex-1 min-w-[200px]`} value={formData.featuredImage} onChange={(e) => handleInputChange('featuredImage', e.target.value)} placeholder="Or enter image URL" />
            </div>
            <p className="text-white/25 text-xs">Supported: JPEG, PNG, GIF, WebP (Max 5MB)</p>
            {formData.featuredImage && (
              <div className="relative w-40 h-24 rounded-lg overflow-hidden border border-white/[0.1]">
                <img src={formData.featuredImage} alt="Featured" className="w-full h-full object-cover" />
                <button type="button" onClick={() => handleInputChange('featuredImage', '')}
                  className="absolute top-1 right-1 w-5 h-5 bg-red/80 hover:bg-red rounded-full flex items-center justify-center text-white text-xs transition-colors">
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="mt-4 space-y-2">
            <label className={LABEL}>Tags</label>
            <div className="flex gap-2">
              <input type="text" className={INPUT} value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                placeholder="Add tag and press Enter" />
              <button type="button" onClick={handleAddTag} className={BTN_SECONDARY}>Add</button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-primary/15 text-primary border border-primary/20">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="text-primary/60 hover:text-primary ml-0.5">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SEO Settings */}
        <div className={CARD}>
          <h2 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">SEO Settings</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {[
              { label: 'SEO Title (English)', field: 'seoTitle' as const, max: 60 },
              { label: 'SEO Title (Spanish)', field: 'seoTitleEs' as const, max: 60 },
            ].map(({ label, field, max }) => (
              <div key={field}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL.replace('mb-1.5', '')}>{label}</label>
                  <span className={`text-xs font-mono ${seoCharCls((formData[field] || '').length, max)}`}>
                    {(formData[field] || '').length}/{max}
                  </span>
                </div>
                <input type="text" className={`${INPUT} ${seoFieldErrors[field] ? 'border-red/40' : ''}`}
                  value={formData[field]} onChange={(e) => handleInputChange(field, e.target.value)} maxLength={max} placeholder="Auto-generated from title" />
                {seoFieldErrors[field] && <p className="text-red text-xs mt-1">{seoFieldErrors[field]}</p>}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            {[
              { label: 'SEO Description (English)', field: 'seoDescription' as const, max: 160 },
              { label: 'SEO Description (Spanish)', field: 'seoDescriptionEs' as const, max: 160 },
            ].map(({ label, field, max }) => (
              <div key={field}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL.replace('mb-1.5', '')}>{label}</label>
                  <span className={`text-xs font-mono ${seoCharCls((formData[field] || '').length, max)}`}>
                    {(formData[field] || '').length}/{max}
                  </span>
                </div>
                <textarea className={`${INPUT} ${seoFieldErrors[field] ? 'border-red/40' : ''}`}
                  value={formData[field]} onChange={(e) => handleInputChange(field, e.target.value)} maxLength={max} rows={3} placeholder="Auto-generated from excerpt" />
                {seoFieldErrors[field] && <p className="text-red text-xs mt-1">{seoFieldErrors[field]}</p>}
              </div>
            ))}
          </div>

          <div className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-4">
            <h3 className="text-xs font-semibold text-white/50 mb-2">SEO Best Practices</h3>
            <ul className="space-y-1 text-white/35 text-xs list-disc list-inside">
              <li><span className="text-white/50">Titles:</span> 50–60 characters for best search results</li>
              <li><span className="text-white/50">Descriptions:</span> 150–160 characters for optimal display</li>
              <li><span className="text-white/50">Keywords:</span> Include relevant search terms naturally</li>
              <li><span className="text-white/50">Uniqueness:</span> Each page should have unique SEO metadata</li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  );
}

export default BlogEditor;
